import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

export type IntegrationStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
export type AuthKind = 'oauth' | 'api_key' | 'generated_basic';
export type IntegrationCategory = 'shipping' | 'marketing' | 'reviews' | 'accounting' | 'support' | 'analytics';

export interface CredentialField {
  key: string;
  label: string;
  type?: 'text' | 'password';
  help?: string;
  placeholder?: string;
}

export interface ProviderSummary {
  id: string;
  name: string;
  description: string;
  docsUrl: string;
  category: IntegrationCategory;
  authKind: AuthKind;
  capabilities: string[];
  events: string[];
  credential_fields: CredentialField[];
  setup_steps: string[];
  status: IntegrationStatus;
  connected_at: string | null;
  last_sync_at: string | null;
  last_event_at: string | null;
  last_error: string | null;
  config: Record<string, unknown>;
  oauth_ready: boolean;
  oauth_missing: string[];
  stats_24h: { ok: number; error: number };
}

export interface SyncLogRow {
  id: string;
  direction: 'outbound' | 'inbound';
  event_type: string | null;
  entity_type: string | null;
  entity_id: string | null;
  status: 'ok' | 'error' | 'skipped';
  error: string | null;
  duration_ms: number | null;
  created_at: string;
}

export const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  shipping: 'Shipping & fulfillment',
  marketing: 'Email & marketing',
  reviews: 'Reviews & UGC',
  accounting: 'Accounting & tax',
  support: 'Customer support',
  analytics: 'Analytics & ads',
};

export const CATEGORY_ORDER: IntegrationCategory[] = [
  'shipping', 'marketing', 'reviews', 'accounting', 'support', 'analytics',
];

const BASE = '/admin/integrations/providers';
export const PROVIDERS_KEY = ['integrations', 'providers'] as const;

export function useIntegrationProviders() {
  return useQuery({
    queryKey: PROVIDERS_KEY,
    queryFn: () => api.get<{ providers: ProviderSummary[] }>(BASE),
    staleTime: 30_000,
  });
}

export function useIntegrationProvider(id: string | undefined) {
  return useQuery({
    queryKey: [...PROVIDERS_KEY, id],
    queryFn: () => api.get<{ provider: ProviderSummary; log: SyncLogRow[] }>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

export function useIntegrationLog(id: string | undefined, status: string, limit = 50) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (status && status !== 'all') qs.set('status', status);
  return useQuery({
    queryKey: [...PROVIDERS_KEY, id, 'log', status, limit],
    queryFn: () => api.get<{ log: SyncLogRow[] }>(`${BASE}/${id}/log?${qs.toString()}`),
    enabled: !!id,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: PROVIDERS_KEY });
}

export function useConnectIntegration(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { credentials?: Record<string, string>; display_name?: string }) =>
      api.post<{ provider: ProviderSummary; credentials?: Record<string, string> }>(`${BASE}/${id}/connect`, body),
    onSuccess: () => void invalidate(),
  });
}

export function useOAuthUrl(id: string) {
  return useMutation({
    mutationFn: (body: { redirect_to?: string } = {}) =>
      api.post<{ auth_url: string }>(`${BASE}/${id}/oauth-url`, body),
  });
}

export function useDisconnectIntegration(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.post<{ provider: ProviderSummary }>(`${BASE}/${id}/disconnect`),
    onSuccess: () => void invalidate(),
  });
}

export function useTestIntegration(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean; detail?: string }>(`${BASE}/${id}/test`),
    onSettled: () => void invalidate(),
  });
}

export function useSyncIntegration(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.post<{ job_id: string }>(`${BASE}/${id}/sync`),
    onSuccess: () => void invalidate(),
  });
}

export function useSaveIntegrationSettings(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<{ provider: ProviderSummary }>(`${BASE}/${id}/settings`, patch),
    onSuccess: () => void invalidate(),
  });
}

export function useRevealCredentials(id: string) {
  return useMutation({
    mutationFn: () => api.get<{ credentials: Record<string, string> }>(`${BASE}/${id}/credentials/reveal`),
  });
}

export function useRotateCredentials(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.post<{ credentials: Record<string, string> }>(`${BASE}/${id}/rotate-credentials`),
    onSuccess: () => void invalidate(),
  });
}

/** Connector-defined admin helper (e.g. list a vendor's accounts for a mapping picker). */
export function useIntegrationAction<T = unknown>(id: string, action: string) {
  return useMutation({
    mutationFn: (input: Record<string, unknown> = {}) =>
      api.post<{ result: T }>(`${BASE}/${id}/actions/${action}`, input).then((r) => r.result),
  });
}
